// Add_Company_Individual.tsx
import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import CustomInput from "@/components/CustomInput/CustomInput";
import { useAddUser } from "@/hooks/useTenantDashboardService";
import { useQueryClient } from "@tanstack/react-query";
import { isValid } from "@/utils/validators";
import { Box, Button } from "@mui/material";
import { toast } from "react-toastify";
import { useUserStore } from "@/store";
import { Eye, EyeOff } from "lucide-react";
import type { AxiosError } from "axios";
import { useDashboardTheme } from "@/pages/MeetingDashboard/themeHelper";

interface ErrorResponse {
  message?: string;
}

type ValidField = "fullName" | "email" | "password" | "companyName";

const INDUSTRIES = [
  "Technology / SaaS",
  "Financial Services",
  "Healthcare",
  "Real Estate / Mortgage",
  "Consumer Sales",
  "Home Services",
  "Automotive",
  "Insurance",
  "Professional Services",
  "Manufacturing / Industrial",
  "Education / Training",
  "Other",
];

const Add_Company_Individual: React.FC = () => {
  const { isDark } = useDashboardTheme();
  const { mode } = useParams<{ mode: string }>();
  const isCompany = mode?.toLowerCase() === "company";
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { mutate: addUser, isPending } = useAddUser();
  const { setPageHeader } = useUserStore();
  const [showPassword, setShowPassword] = useState(false);

  const [formData, setFormData] = useState({
    fullName: "",
    email: "",
    companyName: "",
    password: "",
    role: isCompany ? "ADMIN" : "USER",
  });

  const [selectedIndustries, setSelectedIndustries] = useState<string[]>([]);

  const [errors, setErrors] = useState<Record<ValidField, string>>({
    fullName: "",
    email: "",
    password: "",
    companyName: "",
  });

  const getValidationError = (
    _p0: string,
    _fullName: string,
    field: ValidField,
  ): string => {
    switch (field) {
      case "fullName":
        return "Valid full name required";
      case "email":
        return "Valid email required";
      case "password":
        return "Must be 8+ characters, include upper, lower, number, and symbol.";
      case "companyName":
        return "Valid company name required";
      default:
        return "Invalid input";
    }
  };

  const handleChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
    >,
  ) => {
    const { name, value } = e.target;

    setFormData((prev) => ({ ...prev, [name]: value }));

    if (name === "email") {
      setErrors((prev) => ({ ...prev, email: "" }));
    }

    if (["fullName", "email", "password", "companyName"].includes(name)) {
      const valid = isValid(name as ValidField, value);
      setErrors((prev) => ({
        ...prev,
        [name as ValidField]: valid
          ? ""
          : getValidationError(name, value, name as ValidField),
      }));
    }
  };

  const handleBlur = (
    e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    const { name, value } = e.target as HTMLInputElement;
    if (["fullName", "email", "password", "companyName"].includes(name)) {
      const valid = isValid(name as ValidField, value);
      setErrors((prev) => ({
        ...prev,
        [name as ValidField]: valid
          ? ""
          : getValidationError(name, value, name as ValidField),
      }));
    }
  };

  const handleSave = () => {
    const validationResult: Record<ValidField, string> = {
      fullName: isValid("fullName", formData.fullName)
        ? ""
        : getValidationError("fullName", formData.fullName, "fullName"),
      email: isValid("email", formData.email)
        ? ""
        : getValidationError("email", formData.email, "email"),
      password: isValid("password", formData.password)
        ? ""
        : getValidationError("password", formData.password, "password"),
      companyName: isCompany
        ? isValid("companyName", formData.companyName)
          ? ""
          : getValidationError("companyName", formData.companyName, "companyName")
        : "",
    };

    setErrors(validationResult);

    if (Object.values(validationResult).some((val) => val !== "")) return;

    const tenantType = (isCompany ? "COMPANY" : "INDIVIDUAL") as "COMPANY" | "INDIVIDUAL";

    const payload = {
      email: formData.email,
      name: formData.fullName,
      companyName: isCompany ? formData.companyName : undefined,
      tenantType,
      password: formData.password,
      role: isCompany ? "ADMIN" : formData.role,
      ...(isCompany && selectedIndustries.length > 0 ? { industries: selectedIndustries } : {}),
    };

    addUser(payload, {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ["users"] });
        toast.success("User added successfully!");
        setTimeout(() => {
          navigate("/tenants");
        }, 1000);
      },
      onError: (error) => {
        const err = error as AxiosError<ErrorResponse>;
        const message =
          err?.response?.data?.message || err?.message || "Failed to add user";

        if (
          message.toLowerCase().includes("email") ||
          message.toLowerCase().includes("user already exists")
        ) {
          setErrors((prev) => ({
            ...prev,
            email: "Email already exists. Please use a different email.",
          }));
        } else {
          toast.error(message);
        }
      },
    });
  };

  useEffect(() => {
    if (mode?.toLowerCase() === "company") {
      setPageHeader("Add Company");
    } else if (mode?.toLowerCase() === "individual") {
      setPageHeader("Add Individual");
    }
  }, [mode, setPageHeader]);

  return (
    <div className={`space-y-6 p-5 mt-20 ${isDark ? "" : "light-theme-dashboard"}`} style={{ background: "var(--color-bg)", minHeight: "100vh" }}>
      <div className="bg-[var(--color-surface)] p-6 rounded-2xl border border-[var(--color-border)]">
        <Box display="flex" justifyContent="flex-end" mt={3} gap={3} mb={3}>
          <Button
            onClick={() => navigate("/dashboard")}
            sx={{
              backgroundColor: "#C1FF72",
              color: "#000",
              fontWeight: "bold",
              borderRadius: 999,
              px: 3,
              py: 1,
              textTransform: "none",
              "&:hover": { backgroundColor: "#D7FF9B" },
            }}
          >
            Go Back
          </Button>
        </Box>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
          <CustomInput
            label="Full Name"
            name="fullName"
            value={formData.fullName}
            placeholder="Enter full name"
            onChange={handleChange}
            onBlur={handleBlur}
            error={errors.fullName}
            required
          />

          <CustomInput
            label="Email Address"
            name="email"
            value={formData.email}
            type="email"
            placeholder="Enter email"
            onChange={handleChange}
            onBlur={handleBlur}
            error={errors.email}
            required
          />

          {isCompany && (
            <CustomInput
              label="Company Name"
              name="companyName"
              value={formData.companyName}
              placeholder="Enter company name"
              onChange={handleChange}
              onBlur={handleBlur}
              error={errors.companyName}
              required
            />
          )}

          <div className="relative">
            <CustomInput
              label="Password"
              name="password"
              type={showPassword ? "text" : "password"}
              value={formData.password}
              placeholder="Enter password"
              onChange={handleChange}
              onBlur={handleBlur}
              error={errors.password}
              required
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute right-3 top-9 text-gray-400 hover:text-gray-200"
            >
              {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
            </button>
          </div>

          {!isCompany && (
            <div>
              <label className="block text-sm text-[var(--color-text)] font-bold mb-1">
                Role<span className="text-red-500 ml-1">*</span>
              </label>
              <select
                name="role"
                value={formData.role}
                onChange={handleChange}
                className="bg-[var(--color-surface)] border border-[var(--color-border)] text-[var(--color-text)] rounded-3xl px-4 py-2 w-full focus:outline-none"
              >
                <option value="ADMIN">ADMIN</option>
                <option value="MANAGER">MANAGER</option>
                <option value="USER">USER</option>
              </select>
            </div>
          )}
        </div>

        {isCompany && (
          <div className="mb-6">
            <label className="block text-sm text-[var(--color-text)] font-bold mb-3">
              Seed Practice Scenarios by Industry
            </label>
            <div className="bg-[var(--color-surface)] border border-[var(--color-border)] rounded-3xl p-4">
              <div className="mb-3 pb-3 border-b border-[var(--color-border)] flex items-center gap-2">
                <input
                  type="checkbox"
                  id="selectAll"
                  checked={selectedIndustries.length === INDUSTRIES.length}
                  onChange={(e) => {
                    if (e.target.checked) {
                      setSelectedIndustries(INDUSTRIES);
                    } else {
                      setSelectedIndustries([]);
                    }
                  }}
                  className="w-4 h-4 rounded border border-[var(--color-border)] bg-[var(--color-surface)] focus:ring-0 cursor-pointer accent-[var(--color-stat-accent)]"
                />
                <label htmlFor="selectAll" className="text-[var(--color-text)] text-sm font-bold cursor-pointer select-none">
                  Select All
                </label>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3">
                {INDUSTRIES.map((industry) => (
                  <div key={industry} className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      id={`industry-${industry}`}
                      checked={selectedIndustries.includes(industry)}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setSelectedIndustries((prev) => [...prev, industry]);
                        } else {
                          setSelectedIndustries((prev) => prev.filter((i) => i !== industry));
                        }
                      }}
                      className="w-4 h-4 rounded border border-[var(--color-border)] bg-[var(--color-surface)] focus:ring-0 cursor-pointer accent-[var(--color-stat-accent)]"
                    />
                    <label htmlFor={`industry-${industry}`} className="text-[var(--color-text)] text-sm cursor-pointer select-none">
                      {industry}
                    </label>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        <Box display="flex" justifyContent="flex-end" mt={3} gap={3}>
          <Button
            onClick={handleSave}
            sx={{
              backgroundColor: "#C1FF72",
              color: "#000",
              fontWeight: "bold",
              borderRadius: 999,
              px: 3,
              py: 1,
              textTransform: "none",
              "&:hover": { backgroundColor: "#D7FF9B" },
            }}
          >
            {isPending ? (
              <div className="w-5 h-5 border-2 border-black border-t-transparent rounded-full animate-spin"></div>
            ) : (
              "CREATE"
            )}
          </Button>
        </Box>
      </div>
    </div>
  );
};

export default Add_Company_Individual;
